import * as THREE from 'three';

export type SavannaMaterials = {
  elephantSkin: THREE.MeshStandardMaterial;
  elephantSkinLight: THREE.MeshStandardMaterial;
  elephantSkinDark: THREE.MeshStandardMaterial;
  tusk: THREE.MeshStandardMaterial;
  eye: THREE.MeshPhysicalMaterial;
  earth: THREE.MeshStandardMaterial;
  earthDark: THREE.MeshStandardMaterial;
  redRock: THREE.MeshStandardMaterial;
  bark: THREE.MeshStandardMaterial;
  barkLight: THREE.MeshStandardMaterial;
  dryWood: THREE.MeshStandardMaterial;
  leaf: THREE.MeshStandardMaterial;
  leafLight: THREE.MeshStandardMaterial;
  dryLeaf: THREE.MeshStandardMaterial;
  fruit: THREE.MeshStandardMaterial;
  flower: THREE.MeshStandardMaterial;
  termite: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  thorn: THREE.MeshStandardMaterial;
  reward: THREE.MeshStandardMaterial;
  hazard: THREE.MeshStandardMaterial;
  groundContact: THREE.MeshBasicMaterial;
};

export const SAVANNA_SIGNAL_COLORS = {
  water: new THREE.Color(0x4dbcca),
  reward: new THREE.Color(0xf3ba47),
  healthy: new THREE.Color(0x7fa247),
  hazard: new THREE.Color(0xc96843),
  objective: new THREE.Color(0xf0df8c),
} as const;

export function createSavannaMaterials(): SavannaMaterials {
  return {
    elephantSkin: standard(0x747874, 0.87),
    elephantSkinLight: standard(0x94978f, 0.82),
    elephantSkinDark: standard(0x4d514f, 0.93),
    tusk: standard(0xeee2c5, 0.66),
    eye: new THREE.MeshPhysicalMaterial({
      color: 0x171713,
      roughness: 0.18,
      metalness: 0,
      clearcoat: 0.7,
      clearcoatRoughness: 0.16,
    }),
    earth: standard(0xb78c52, 0.96),
    earthDark: standard(0x745036, 1),
    redRock: standard(0x9b5538, 0.91),
    bark: standard(0x58422d, 0.98),
    barkLight: standard(0x816044, 0.96),
    dryWood: standard(0x9a7a52, 1),
    leaf: standard(0x4f7137, 0.88),
    leafLight: standard(0x7f9a45, 0.84),
    dryLeaf: standard(0xb69a52, 0.96),
    fruit: new THREE.MeshStandardMaterial({
      color: 0xe59639,
      roughness: 0.68,
      metalness: 0,
      emissive: 0x4a1d08,
      emissiveIntensity: 0.12,
    }),
    flower: new THREE.MeshStandardMaterial({
      color: 0xe8d675,
      roughness: 0.72,
      metalness: 0,
      emissive: 0x624513,
      emissiveIntensity: 0.1,
    }),
    termite: standard(0xa85f38, 0.94),
    stone: standard(0x6d675b, 0.93),
    thorn: standard(0x3e4c2c, 0.96),
    reward: new THREE.MeshStandardMaterial({
      color: SAVANNA_SIGNAL_COLORS.reward,
      roughness: 0.46,
      emissive: 0x4f2e08,
      emissiveIntensity: 0.18,
    }),
    hazard: new THREE.MeshStandardMaterial({
      color: SAVANNA_SIGNAL_COLORS.hazard,
      roughness: 0.7,
      emissive: 0x421109,
      emissiveIntensity: 0.14,
    }),
    groundContact: new THREE.MeshBasicMaterial({
      color: 0x3b2b20,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  };
}

export function disposeSavannaMaterials(materials: SavannaMaterials): void {
  const unique = new Set<THREE.Material>(Object.values(materials));
  for (const material of unique) material.dispose();
}

function standard(color: THREE.ColorRepresentation, roughness: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0 });
}
